import React, { useState, useRef, useCallback } from 'react';
import { usePlayer } from '../PlayerProvider';

interface ProgressBarProps {
  className?: string;
  showTime?: boolean;
  height?: 'thin' | 'medium' | 'thick';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ 
  className = '',
  showTime = true,
  height = 'medium' 
}) => {
  const { currentTime, duration, seekTo, formatTime, getProgressPercentage } = usePlayer();
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);
  const progressBarRef = useRef<HTMLDivElement>(null);
  
  const progressPercentage = getProgressPercentage();

  React.useEffect(() => {
    console.log('🎵 ProgressBar - duration changed:', duration, 'currentTime:', currentTime);
  }, [duration]);

  const heightClasses = {
    thin: 'h-1',
    medium: 'h-2',
    thick: 'h-3',
  };

  const getTimeAtPosition = useCallback((position: number): number => {
    return (position / 100) * duration;
  }, [duration]);

  const getPositionFromEvent = useCallback((event: React.MouseEvent | React.TouchEvent): number => {
    if (!progressBarRef.current) return 0;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const position = ((clientX - rect.left) / rect.width) * 100;
    
    return Math.max(0, Math.min(100, position));
  }, []);

  const handleClick = useCallback((event: React.MouseEvent) => {
    if (!duration) return;

    const position = getPositionFromEvent(event);
    const time = getTimeAtPosition(position);
    seekTo(time);
  }, [duration, getPositionFromEvent, getTimeAtPosition, seekTo]);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    const position = getPositionFromEvent(event);
    setHoverPosition(position);
  }, [getPositionFromEvent]);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!duration) return;

    setIsDragging(true);
    handleClick(event);

    const handleMouseMove = (e: MouseEvent) => {
      if (!progressBarRef.current || !duration) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const position = ((e.clientX - rect.left) / rect.width) * 100;
      const clampedPosition = Math.max(0, Math.min(100, position));
      const time = getTimeAtPosition(clampedPosition);

      seekTo(time);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [duration, handleClick, getTimeAtPosition, seekTo]);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    if (!duration) return;

    setIsDragging(true);
    const position = getPositionFromEvent(event);
    const time = getTimeAtPosition(position);
    seekTo(time);
  }, [duration, getPositionFromEvent, getTimeAtPosition, seekTo]);
  
  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    if (!duration || !isDragging) return;
    
    event.preventDefault();
    const position = getPositionFromEvent(event);
    const time = getTimeAtPosition(position);
    seekTo(time);
  }, [duration, isDragging, getPositionFromEvent, getTimeAtPosition, seekTo]);
  
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  return (
    <div className={`progress-bar-container flex items-center space-x-3 ${className}`}>
      {showTime && (
        <div className="text-xs font-mono text-white min-w-[40px] text-right">
          {formatTime(currentTime)}
        </div>
      )}

      <div
        ref={progressBarRef}
        className={`
          progress-bar relative flex-1 bg-neutral-600 bg-opacity-30 rounded-full cursor-pointer
          transition-all duration-200 group
          ${heightClasses[height]}
          ${isHovering || isDragging ? 'bg-opacity-50' : ''}
        `}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          if (!isDragging) setIsDragging(false);
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={currentTime}
        aria-label={`Progress: ${formatTime(currentTime)} of ${formatTime(duration)}`}
      >
        <div
          className={`
            progress-fill absolute left-0 top-0 h-full rounded-full transition-all duration-100
            bg-gradient-to-r from-meradio-primary to-meradio-secondary
            ${isDragging ? 'transition-none' : ''}
          `}
          style={{ width: `${progressPercentage}%` }}
        />
        
        {isHovering && !isDragging && duration > 0 && (
          <>
            <div
              className="absolute top-0 w-0.5 h-full bg-white bg-opacity-60 pointer-events-none"
              style={{ left: `${hoverPosition}%` }}
            />

            <div
              className="
                absolute bottom-full mb-2 px-2 py-1 text-xs bg-black bg-opacity-75 text-white
                rounded whitespace-nowrap transform -translate-x-1/2 pointer-events-none
              "
              style={{ left: `${hoverPosition}%` }}
            >
              {formatTime(getTimeAtPosition(hoverPosition))}
            </div>
          </>
        )}

        <div
          className={`
            progress-thumb absolute top-1/2 w-4 h-4 bg-white rounded-full shadow-lg
            transform -translate-y-1/2 -translate-x-1/2 transition-all duration-200
            ${(isHovering || isDragging) && duration > 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
            ${isDragging ? 'scale-125 shadow-xl' : ''}
          `}
          style={{ left: `${progressPercentage}%` }}
        />

        <div
          className="absolute left-0 top-0 h-full bg-white bg-opacity-20 rounded-full pointer-events-none"
          style={{ width: `${Math.min(100, progressPercentage + 10)}%` }}
        />
      </div>

      {showTime && (
        <div className="text-xs font-mono text-white min-w-[40px]">
          {formatTime(duration)}
        </div>
      )}
    </div>
  );
};

export default ProgressBar;
